import React, {useContext, useEffect, useMemo, useRef} from 'react';
import {Animated, Modal, Pressable, StyleSheet, Text, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import {useTranslation} from "react-i18next";
import {TourContext} from '../helpers/TourContext';

const AppTutorialOverlay = () => {
    const { t } = useTranslation();
    const {
        appTutorialActive,
        appTutorialSteps,
        appTutorialStepIndex,
        activeAppTutorialStep,
        nextAppTutorialStep,
        previousAppTutorialStep,
        skipAppTutorialSection,
        endAppTutorial,
    } = useContext(TourContext);
    const fade = useRef(new Animated.Value(0)).current;
    const slide = useRef(new Animated.Value(18)).current;

    useEffect(() => {
        let watchdog;

        Animated.parallel([
            Animated.timing(fade, {
                toValue: appTutorialActive ? 1 : 0,
                duration: appTutorialActive ? 220 : 160,
                // JS-driven, NOT the native driver: a native-driver animation started
                // in the same frame an iOS <Modal> presents is silently dropped, which
                // would leave the card stuck at opacity 0 while the modal still swallows
                // every touch → whole app frozen after login (no hardware back on iOS).
                useNativeDriver: false,
            }),
            Animated.spring(slide, {
                toValue: appTutorialActive ? 0 : 18,
                friction: 9,
                tension: 70,
                useNativeDriver: false,
            }),
        ]).start();

        if (appTutorialActive) {
            // Safety net: if the entrance animation is ever dropped/janked, snap the
            // card to its visible, interactive resting state so it can never end up
            // invisible-but-touch-blocking.
            watchdog = globalThis.setTimeout(() => {
                fade.setValue(1);
                slide.setValue(0);
            }, 500);
        }

        return () => globalThis.clearTimeout(watchdog);
    }, [appTutorialActive, fade, slide]);

    const sectionProgress = useMemo(() => {
        if (!activeAppTutorialStep?.section) {
            return null;
        }

        const sectionSteps = appTutorialSteps.filter((step) => step.section === activeAppTutorialStep.section);
        const sectionIndex = sectionSteps.findIndex((step) => step.id === activeAppTutorialStep.id);

        return {
            index: sectionIndex + 1,
            total: sectionSteps.length,
        };
    }, [activeAppTutorialStep, appTutorialSteps]);

    if (!appTutorialActive || !activeAppTutorialStep) {
        return null;
    }

    const isFirst = appTutorialStepIndex === 0;
    const isLast = appTutorialStepIndex >= appTutorialSteps.length - 1;
    const progress = `${appTutorialStepIndex + 1}/${appTutorialSteps.length}`;

    return (
        <Modal transparent visible={appTutorialActive} animationType="none" statusBarTranslucent onRequestClose={() => endAppTutorial({markSeen: true})}>
            <View style={styles.overlay} pointerEvents="box-none">
                <Pressable style={styles.scrim} onPress={() => {}} />
                <Animated.View style={[styles.cardWrap, {opacity: fade, transform: [{translateY: slide}]}]}>
                    <LinearGradient
                        colors={['#111827', '#1F2937', '#111827']}
                        start={{x: 0, y: 0}}
                        end={{x: 1, y: 1}}
                        style={styles.card}
                    >
                        <View style={styles.headerRow}>
                            <View style={styles.iconBadge}>
                                <MaterialCommunityIcons name="map-search-outline" size={22} color="#FBBF24" />
                            </View>
                            <View style={styles.headerTextWrap}>
                                <Text style={styles.eyebrow}>{activeAppTutorialStep.section || t('tutorial_eyebrow')}</Text>
                                <Text style={styles.progressText}>{progress}{sectionProgress ? ` · ${t('tutorial_section_progress', { index: sectionProgress.index, total: sectionProgress.total })}` : ''}</Text>
                            </View>
                            <Pressable onPress={() => endAppTutorial({markSeen: true})} hitSlop={10} style={styles.closeButton}>
                                <MaterialCommunityIcons name="close" size={20} color="#E5E7EB" />
                            </Pressable>
                        </View>

                        <Text style={styles.title}>{activeAppTutorialStep.title}</Text>
                        <Text style={styles.body}>{activeAppTutorialStep.body}</Text>

                        <View style={styles.progressBarTrack}>
                            <View style={[styles.progressBarFill, {width: `${((appTutorialStepIndex + 1) / appTutorialSteps.length) * 100}%`}]} />
                        </View>

                        <View style={styles.actionsRow}>
                            <Pressable
                                disabled={isFirst}
                                onPress={previousAppTutorialStep}
                                style={[styles.secondaryButton, isFirst && styles.disabledButton]}
                            >
                                <Text style={[styles.secondaryButtonText, isFirst && styles.disabledButtonText]}>{t('tutorial_back')}</Text>
                            </Pressable>
                            <Pressable onPress={skipAppTutorialSection} style={styles.secondaryButton}>
                                <Text style={styles.secondaryButtonText}>{t('tutorial_skip_section')}</Text>
                            </Pressable>
                            <Pressable
                                onPress={isLast ? () => endAppTutorial({markSeen: true}) : nextAppTutorialStep}
                                style={styles.primaryButton}
                            >
                                <Text style={styles.primaryButtonText}>{isLast ? t('tutorial_finish') : t('tutorial_next')}</Text>
                            </Pressable>
                        </View>
                    </LinearGradient>
                </Animated.View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        justifyContent: 'flex-end',
    },
    scrim: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'rgba(15, 23, 42, 0.34)',
    },
    cardWrap: {
        paddingHorizontal: 14,
        paddingBottom: 18,
    },
    card: {
        borderRadius: 24,
        padding: 18,
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 12},
        shadowOpacity: 0.24,
        shadowRadius: 24,
        elevation: 12,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.12)',
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 14,
    },
    iconBadge: {
        width: 42,
        height: 42,
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(251,191,36,0.12)',
        marginRight: 10,
    },
    headerTextWrap: {
        flex: 1,
        minWidth: 0,
    },
    eyebrow: {
        color: '#FBBF24',
        fontSize: 11,
        fontWeight: '900',
        textTransform: 'uppercase',
        letterSpacing: 1.2,
    },
    progressText: {
        color: '#CBD5E1',
        fontSize: 12,
        fontWeight: '700',
        marginTop: 2,
    },
    closeButton: {
        width: 34,
        height: 34,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255,255,255,0.08)',
    },
    title: {
        color: '#FFFFFF',
        fontSize: 21,
        lineHeight: 27,
        fontWeight: '900',
        letterSpacing: -0.2,
    },
    body: {
        color: '#E5E7EB',
        fontSize: 14,
        lineHeight: 21,
        fontWeight: '600',
        marginTop: 9,
    },
    progressBarTrack: {
        height: 7,
        borderRadius: 99,
        backgroundColor: 'rgba(255,255,255,0.12)',
        overflow: 'hidden',
        marginTop: 16,
    },
    progressBarFill: {
        height: '100%',
        borderRadius: 99,
        backgroundColor: '#FBBF24',
    },
    actionsRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        gap: 8,
        marginTop: 16,
    },
    secondaryButton: {
        minHeight: 42,
        borderRadius: 14,
        paddingHorizontal: 12,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255,255,255,0.08)',
    },
    secondaryButtonText: {
        color: '#F8FAFC',
        fontSize: 13,
        fontWeight: '800',
    },
    primaryButton: {
        minHeight: 42,
        borderRadius: 14,
        paddingHorizontal: 16,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#FBBF24',
    },
    primaryButtonText: {
        color: '#1F2937',
        fontSize: 13.5,
        fontWeight: '900',
    },
    disabledButton: {
        opacity: 0.45,
    },
    disabledButtonText: {
        color: '#CBD5E1',
    },
});

export default AppTutorialOverlay;



